import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { getMyProfile, getProfileById, updateMyProfile } from '../../../../services/profileService';
import type { IProfile } from '../../../../types/profile';
import { useFormik } from 'formik';
import { updateProfileSchema } from '../../../../forms/updateProfileSchema';
import FormField from '../../../../components/common/FormField';
import Loader from '../../../../components/common/Loader';
// import { showToast } from '../../../../utils/showToast'; // ✅ Removed showToast
import { useParams } from 'react-router-dom';
import Modal from '../../../../components/common/Modal'; // ✅ Import Modal component

const Profile: React.FC = () => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { id: profileIdParam } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<IProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ✅ New state for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');


  const formik = useFormik({
    initialValues: {
      name: profile?.name || '',
      email: profile?.email || '',
      phone: profile?.phone || '',
      address: profile?.address || '',
      education: {
        bachelor: {
          collegeName: profile?.education?.bachelor?.collegeName || '',
          degree: profile?.education?.bachelor?.degree || '',
          graduationYear: profile?.education?.bachelor?.graduationYear || '',
        },
        master: {
          collegeName: profile?.education?.master?.collegeName || '',
          degree: profile?.education?.master?.degree || '',
          graduationYear: profile?.education?.master?.graduationYear || '',
        },
      },
    },
    validationSchema: updateProfileSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          ...values,
          education: {
            bachelor: {
              ...values.education.bachelor,
              graduationYear: Number(values.education.bachelor.graduationYear),
            },
            master: {
              ...values.education.master,
              graduationYear: Number(values.education.master.graduationYear),
            },
          },
        };

        await updateMyProfile(payload);
        setModalTitle('Profile Updated Successfully');
        setModalMessage('Your profile information has been saved.');
        setIsModalOpen(true);
      } catch (err: any) {
        setModalTitle('Profile Update Failed');
        setModalMessage(err.message || 'An unexpected error occurred during update.');
        setIsModalOpen(true);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      const userIdToFetch = profileIdParam || user?._id;

      if (isAuthenticated && userIdToFetch) {
        setIsLoading(true);
        try {
          const profileData = profileIdParam ? await getProfileById(userIdToFetch) : await getMyProfile();
          setProfile(profileData);
        } catch (err: any) {
          setError('Failed to load profile: ' + err.message);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchProfileData();
  }, [isAuthenticated, profileIdParam, user?._id]);


  if (isAuthLoading || isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <p className="text-red-600 font-medium">Error: {error}</p>
      </div>
    );
  }

  const isEditable = !profileIdParam || (user?._id === profileIdParam) || user?.isAdmin;


  return (
    <div className="py-10 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {profileIdParam ? `Viewing Profile: ${profile?.name}` : 'Edit Your Profile'}
        </h1>
        {isEditable ? (
            <form onSubmit={formik.handleSubmit} className="space-y-6">
            <FormField
                label="Full Name"
                name="name"
                type="text"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && formik.errors.name ? formik.errors.name : ''}
                disabled={!isEditable}
            />
            <FormField
                label="Email"
                name="email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
                disabled={!isEditable}
            />
            <FormField
                label="Phone"
                name="phone"
                type="tel"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && formik.errors.phone ? formik.errors.phone : ''}
                disabled={!isEditable}
            />
            <FormField
                label="Address"
                name="address"
                type="text"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && formik.errors.address ? formik.errors.address : ''}
                disabled={!isEditable}
            />
            <div className="border-t pt-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Education</h2>
                <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Bachelor's Degree</h3>
                <FormField
                    label="College Name"
                    name="education.bachelor.collegeName"
                    type="text"
                    value={formik.values.education.bachelor.collegeName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.education?.bachelor?.collegeName && formik.errors.education?.bachelor?.collegeName ? formik.errors.education?.bachelor?.collegeName : ''}
                    disabled={!isEditable}
                />
                <FormField
                    label="Degree"
                    name="education.bachelor.degree"
                    type="text"
                    value={formik.values.education.bachelor.degree}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.education?.bachelor?.degree && formik.errors.education?.bachelor?.degree ? formik.errors.education?.bachelor?.degree : ''}
                    disabled={!isEditable}
                />
                <FormField
                    label="Graduation Year"
                    name="education.bachelor.graduationYear"
                    type="number"
                    value={formik.values.education.bachelor.graduationYear.toString()}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.education?.bachelor?.graduationYear && formik.errors.education?.bachelor?.graduationYear ? formik.errors.education?.bachelor?.graduationYear : ''}
                    disabled={!isEditable}
                />
                </div>
            </div>
            {isEditable && (
                <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {formik.isSubmitting ? <Loader /> : <span>Update Profile</span>}
                </button>
            )}
            </form>
        ) : (
            <p className="text-center text-gray-500">You do not have permission to edit this profile.</p>
        )}
      </div>
      {/* ✅ Modal component */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} // Just close the modal on click
        title={modalTitle}
      >
        <p className="text-gray-700">{modalMessage}</p>
        <div className="mt-4 flex justify-end">
            <button
                onClick={() => setIsModalOpen(false)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
                OK
            </button>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
